import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { LocationsApi } from '@frontend/api-locations';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  Button,
  SearchField,
  SpinningLoader,
  useDebouncedValue,
  useFormField,
} from '@frontend/design-system';
import {
  LocationServiceLocation,
  LocationServiceLocationItem,
  LocationServiceValidLocation,
  LocationWithChildren,
} from '../../types/location.types';
import { LocationItem, ParentLocationItem } from './location-picker-item';

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    width: 100%;
    height: 100%;
    overflow: hidden;
  `,
  searchInput: css`
    margin-top: 6px;
  `,
  footer: css`
    display: flex;
    gap: ${theme.spacing(2)};
    padding-top: ${theme.spacing(3)};
    padding-left: ${theme.spacing(3)};
    padding-right: ${theme.spacing(3)};
    margin-left: -${theme.spacing(3)};
    margin-right: -${theme.spacing(3)};
    justify-content: flex-end;
    align-items: center;
    border-top: 1px solid ${theme.colors.neutral10};
  `,
  content: css`
    flex-grow: 1;
    overflow-y: auto;
  `,
  loading: css`
    height: 100%;
    min-height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  itemSection: css`
    margin-bottom: ${theme.spacing(2)};
  `,
};

export type ItemSection = {
  heading: React.ReactNode;
  items: LocationServiceLocationItem[];
};

export type LocationPickerProps = {
  hideCloseActions?: boolean;
  currentLocation: LocationServiceLocation | null;
  onChangeLocation: (location: LocationServiceLocationItem) => void;
  onClose?: () => void;
  itemSections: ItemSection[];
};

export const LocationPicker = ({
  currentLocation,
  hideCloseActions,
  onChangeLocation,
  onClose,
  itemSections,
}: LocationPickerProps) => {
  const searchFieldProps = useFormField({ type: 'text' });
  const searchValue = useDebouncedValue(searchFieldProps.value);

  const [isLoading, setLoading] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState<LocationServiceLocationItem | null>();

  /**
   * All locations queried from endpoint.
   */
  const [validLocations, setValidLocations] = useState<LocationServiceValidLocation[]>([]);

  /**
   * Locations queried from endpoint and formatted with { children: []}.
   */
  const parentLocations = useMemo(() => {
    const { parents, children } = validLocations.reduce(
      (acc, l) => {
        if (!!l.parentId) {
          acc.children.push(l);
        } else {
          acc.parents.push({ ...l, children: [] });
        }

        return acc;
      },
      { parents: [] as LocationWithChildren[], children: [] as LocationServiceValidLocation[] }
    );

    children.forEach((child) => {
      const parentIndex = parents.findIndex((p) => p.locationId === child.parentId);
      if (parentIndex !== -1) {
        parents[parentIndex].children.push(child);
      } else {
        parents.push({ ...child, children: [] });
      }
    });

    return parents;
  }, [validLocations]);

  const handleSelectLocation = useCallback((location: LocationServiceLocationItem) => {
    setSelectedLocation(location || null);
  }, []);

  const handleApplyLocation = useCallback(() => {
    if (selectedLocation?.locationId) {
      onChangeLocation(selectedLocation);
    }
  }, [selectedLocation]);

  const locationIsSelected = useCallback(
    (locationId: string) => {
      return selectedLocation?.locationId === locationId;
    },
    [selectedLocation]
  );

  const locationIsCurrent = useCallback(
    (locationId: string) => {
      return currentLocation?.locationId === locationId;
    },
    [currentLocation]
  );

  useEffect(() => {
    if (currentLocation?.locationId && !selectedLocation?.locationId) {
      setSelectedLocation({
        locationId: currentLocation.locationId,
        name: currentLocation.name,
      });
    }
  }, [currentLocation, selectedLocation]);

  useEffect(() => {
    if (!searchValue) {
      setValidLocations([]);
      return;
    }
    setLoading(true);
    LocationsApi.getPrivilegedLocations(searchValue)
      .then(({ locations }) => {
        const validLocations = (locations?.filter((l) => !!l.locationId || []) || []) as LocationServiceValidLocation[];
        setValidLocations(validLocations);
      })
      .finally(() => setLoading(false));
  }, [searchValue]);

  return (
    <div className='location-picker' css={styles.root}>
      <SearchField
        name='location-search'
        label='Search Location'
        css={styles.searchInput}
        {...searchFieldProps}
        onFocus={(e) => {
          searchFieldProps.onFocus(e);
        }}
        onBlur={(e) => {
          searchFieldProps.onBlur(e);
        }}
        onChange={(e) => {
          searchFieldProps.onChange(e);
        }}
      />

      <div className='location-picker__content' css={styles.content}>
        {isLoading ? (
          <div css={styles.loading}>
            <SpinningLoader size='large' />
          </div>
        ) : (
          <>
            <div css={styles.itemSection}>
              {!!parentLocations.length && (
                <div>
                  <h3>Search Results</h3>
                  {parentLocations.map((location) => (
                    <ParentLocationItem
                      key={location.locationId}
                      location={location}
                      onSelect={handleSelectLocation}
                      isSelected={locationIsSelected}
                      isCurrent={locationIsCurrent}
                    />
                  ))}
                </div>
              )}
              {!parentLocations.length && (
                <>
                  {searchValue ? (
                    <BannerNotification status='warn'>No locations found</BannerNotification>
                  ) : (
                    <BannerNotification status='info'>Search location by name or ID to begin</BannerNotification>
                  )}
                </>
              )}
            </div>
            {!searchValue &&
              itemSections.map((section, index) => {
                if (!section.items.length) {
                  return null;
                }

                return (
                  <div css={styles.itemSection} key={index}>
                    <h3>{section.heading}</h3>
                    {section.items.map((item) => (
                      <LocationItem
                        key={item.locationId}
                        location={item}
                        onSelect={handleSelectLocation}
                        isSelected={locationIsSelected}
                        isCurrent={locationIsCurrent}
                      />
                    ))}
                  </div>
                );
              })}
          </>
        )}
      </div>

      <div className='location-picker__footer' css={styles.footer}>
        {!hideCloseActions && (
          <div>
            <Button variant='secondary' size='large' onClick={onClose}>
              Cancel
            </Button>
          </div>
        )}
        <div>
          <Button size='large' variant='primary' disabled={!selectedLocation?.locationId} onClick={handleApplyLocation}>
            Select Location
          </Button>
        </div>
      </div>
    </div>
  );
};
